/* eslint-disable react/no-multi-comp */
import React, { useCallback, useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, selectUserDetails, showNotifier } from 'containers/app/appSlice';
import { useLazyGetInvoiceByIdV2Query, useMarkPaidMutation } from 'containers/invoices/api';
import { VoidFn, clevertapEvents } from 'types/baseTypes';
import { NotifierTypes } from 'constants/notifierConstants';
import { SidePanelTypes } from 'constants/sidePanelConstants';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { SidePanel } from 'components';
import { logAnalyticEvent } from 'utils/analytics';
import { constructArrayObjectAsString, getPageName, throttle } from 'utils/commonUtils';
import {
  DeviceFingerprintEventType,
  buildDeviceFingerprintBody,
  useSendDeviceFingerprintMutation
} from 'utils/device-fingerprint';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { SidePanelRouteState } from 'routes/types';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE, FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { PaymentCategoryTypes } from 'containers/settings/type';
import RecordPaymentMethodSelection from './RecordPaymentMethodSelection';
import RecordPaymentDetails from './RecordPaymentDetails';
import RecordPaymentSuccess from './RecordPaymentSuccess';
import RecordTipModal from './RecordTipModal';
import { RecordPaymentEditAmount } from './RecordPaymentEditAmount';
import { getPaymentMethodFromId } from 'containers/home/utils';
import { TipTimingEnum } from '../constants';

interface RecordPaymentPanelProps {
  onClose?: VoidFn;
  routeToNextPage?: ({ type }: { type: SidePanelTypes; id: string }) => void;
  isRootElement?: boolean;
}

enum PageState {
  METHOD_SELECTION = 'methodSelection',
  PAYMENT_DETAILS = 'paymentDetails',
  EDIT_AMOUNT = 'editAmount',
  SUCCESS = 'success'
}

const RecordPaymentPanel: React.FC<RecordPaymentPanelProps> = () => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentUser = useAppSelector(selectUserDetails);
  const [pageState, setPageState] = useState<PageState>(PageState.METHOD_SELECTION);
  const dispatch = useAppDispatch();

  const [selectedMethodId, setSelectedMethodId] = React.useState<string | null>();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [referenceNote, setReferenceNote] = useState('');
  const [showTipModal, setShowTipModal] = useState(false);
  const [recordedPaymentAmount, setRecordedPaymentAmount] = useState<number>(0);
  const [tipRecorded, setTipRecorded] = useState<number>(0);

  const [markPaid, { isSuccess: markPaidSuccess, isError: markPaidError, isLoading: markPaidLoading }] =
    useMarkPaidMutation();
  const [sendDeviceFingerprint] = useSendDeviceFingerprintMutation();

  const {
    rootInvoiceId,
    invoiceId: invoiceIdFromUrl,
    rootCustomerId,
    rootEstimateId,
    rootReceiptId,
    rootPage
  } = useParams();

  const [getInvoiceByIdQuery, getInvoiceByIdState] = useLazyGetInvoiceByIdV2Query();
  const {
    data: invoiceDataResponse,
    isSuccess: invoiceDataSuccess,
    isFetching: fetchingInvoiceData
  } = getInvoiceByIdState;
  const invoiceData = invoiceDataResponse?.invoice;

  const { data: merchantCustomisations } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });

  const { paymentModes } = merchantCustomisations || {};

  const hasManualPaymentMethod = paymentModes?.some(
    item => item.category.type === PaymentCategoryTypes.MANUAL && item.enabled
  );
  const { handleSidePanelClose } = useOutletContext<{
    handleSidePanelClose: VoidFn;
    resetInvoicesQuery: VoidFn;
  }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [isRootState, setIsRootState] = useState(true);

  useEffect(() => {
    if (currentTeam?.id) {
      if (invoiceIdFromUrl) {
        getInvoiceByIdQuery({ invoiceId: invoiceIdFromUrl, teamId: currentTeam.id });
      } else if (rootInvoiceId) {
        getInvoiceByIdQuery({ invoiceId: rootInvoiceId, teamId: currentTeam.id });
      }
    }
  }, [invoiceIdFromUrl, rootInvoiceId, currentTeam]);

  useEffect(() => {
    const routeState: SidePanelRouteState = location?.state ?? {};
    if (routeState?.fromSidePanel) {
      setIsRootState(false);
    }
    let routeStateTimeout;
    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location]);

  useEffect(() => {
    if (invoiceData?.invoiceId) {
      logAnalyticEvent(clevertapEvents.WebInvoicesListMarkAsPaidPanelOpen, {
        'Line Items': constructArrayObjectAsString(invoiceData.lineItems, 'Line Item'),
        Page: getPageName(location.pathname)
      });
    }
    if (invoiceData?.isPaid) {
      handleSidePanelBack();
    }
  }, [invoiceData?.invoiceId]);

  const onRecordPayment = () => {
    if (!invoiceData?.invoiceId || !selectedMethodId) {
      dispatch(
        showNotifier({
          type: NotifierTypes.ERROR,
          message: {
            primaryMessage: 'Please ensure all required fields are filled',
            isMessageHtml: true
          }
        })
      );
      return;
    }

    const finalAmount = (selectedAmount || Number(invoiceData?.pendingAmount)) + tipRecorded;

    if (finalAmount <= 0) {
      dispatch(
        showNotifier({
          type: NotifierTypes.ERROR,
          message: {
            primaryMessage: 'Payment amount must be greater than 0',
            isMessageHtml: true
          }
        })
      );
      return;
    }

    setRecordedPaymentAmount(finalAmount);

    markPaid({
      invoiceId: invoiceData.invoiceId,
      teamId: currentTeam.id.toString(),
      paymentMethod: selectedMethodId,
      note: referenceNote,
      paymentAmount: finalAmount,
      tipAmount: tipRecorded
    });
  };

  const recordPaymentHandler = useCallback(throttle(onRecordPayment, 500), [
    invoiceData?.invoiceId,
    currentTeam.id,
    selectedMethodId,
    referenceNote,
    selectedAmount,
    tipRecorded
  ]);

  useEffect(() => {
    if (markPaidError) {
      dispatch(
        showNotifier({
          type: NotifierTypes.ERROR,
          message: {
            primaryMessage: 'Payment Already Initiated',
            isMessageHtml: true
          }
        })
      );
    }
  }, [markPaidError]);

  useEffect(() => {
    if (markPaidSuccess) {
      setPageState(PageState.SUCCESS);
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Payment recorded successfully',
            secondaryMessage: '',
            isMessageHtml: true
          },
          type: NotifierTypes.INFO,
          showClose: false,
          fontStyle: 'text-primary font-normal'
        })
      );
      logAnalyticEvent(clevertapEvents.WebInvoicesListMarkAsPaidPaymentMethod, {
        'Payment Method': getPaymentMethodFromId(selectedMethodId),
        'Invoice Id': invoiceData?.invoiceId
      });
      void buildDeviceFingerprintBody(DeviceFingerprintEventType.InvoiceMarkAsPaid, {
        teamId: currentTeam?.id,
        merchantId: currentUser?.id ?? ''
      }).then(body => sendDeviceFingerprint(body));
    }
  }, [markPaidSuccess]);

  const handleEditAmount = () => {
    setPageState(PageState.EDIT_AMOUNT);
  };

  const handleEditAmountBack = () => {
    setPageState(PageState.PAYMENT_DETAILS);
  };

  const handleEditAmountSave = (amount: number) => {
    const isNotRemainingAmount = amount !== Number(invoiceData?.pendingAmount || invoiceData?.totalAmount);
    setSelectedAmount(isNotRemainingAmount ? amount : null);
    if (tipRecorded) {
      if (
        isNotRemainingAmount &&
        invoiceData?.tip?.enabled &&
        invoiceData.tip.type === TipTimingEnum.FINAL_PAYMENT_ONLY
      ) {
        setTipRecorded(0);
      } else if (tipRecorded >= amount) {
        setTipRecorded(0.99 * amount);
      }
    }
    setPageState(PageState.PAYMENT_DETAILS);
  };

  const onShareReceipt = () => {
    if (rootInvoiceId) {
      if (invoiceIdFromUrl) {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice/${invoiceIdFromUrl}`, {
          state: DEFAULT_SIDE_PANEL_ROUTE_STATE,
          replace: true
        });
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice`, {
          state: DEFAULT_SIDE_PANEL_ROUTE_STATE,
          replace: true
        });
      }
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    }
  };

  const handleSidePanelBack = () => {
    if (pageState === PageState.PAYMENT_DETAILS) {
      setPageState(PageState.METHOD_SELECTION);
    } else if (pageState === PageState.EDIT_AMOUNT) {
      setPageState(PageState.PAYMENT_DETAILS);
    } else if (isRootState) {
      handleSidePanelClose();
    } else {
      navigate(-1);
      setPageState(PageState.METHOD_SELECTION);
    }
  };

  const handleMethodSelectBack = () => {
    setPageState(PageState.METHOD_SELECTION);
  };

  const handleCancelShareInvoice = () => {
    const routeState = location?.state ?? {};
    if (isRootState) {
      handleSidePanelClose();
    } else if (routeState?.counter !== undefined) {
      navigate(-routeState.counter);
    } else if (routeState?.routedFromSecondaryPanel) {
      navigate(-2);
    } else {
      navigate(-1);
    }
  };

  const handleMethodSelect = (methodId: string) => {
    setSelectedMethodId(methodId);
    setPageState(PageState.PAYMENT_DETAILS);
  };

  const handleOpenRecordTipModal = () => {
    setShowTipModal(true);
  };

  const handleTipModalClose = () => {
    setShowTipModal(false);
  };

  const handleTipRecorded = (tipAmount: number) => {
    setTipRecorded(tipAmount);
    setShowTipModal(false);
  };

  // Get selected amount from navigation state if returning from edit amount
  useEffect(() => {
    const routeState = location?.state ?? {};
    if (routeState?.selectedAmount) {
      setSelectedAmount(routeState.selectedAmount);
      // If we have a selected amount and a method, we should be in payment details state
    }
    if (selectedMethodId) {
      setPageState(PageState.PAYMENT_DETAILS);
    }
  }, [location?.state, selectedMethodId]);

  const renderCurrentScreen = () => {
    switch (pageState) {
      case PageState.METHOD_SELECTION:
        return (
          <RecordPaymentMethodSelection
            isRootState={isRootState}
            handleSidePanelBack={handleSidePanelBack}
            invoiceData={invoiceData}
            hasManualPaymentMethod={hasManualPaymentMethod}
            onMethodSelect={handleMethodSelect}
          />
        );
      case PageState.PAYMENT_DETAILS:
        return (
          <RecordPaymentDetails
            isRootState={isRootState}
            handleSidePanelBack={handleSidePanelBack}
            invoiceData={invoiceData}
            selectedMethodId={selectedMethodId}
            selectedAmount={selectedAmount}
            referenceNote={referenceNote}
            handleReferenceNoteChange={setReferenceNote}
            handleEditAmount={handleEditAmount}
            recordPaymentHandler={recordPaymentHandler}
            markPaidLoading={markPaidLoading}
            handleOpenRecordTipModal={handleOpenRecordTipModal}
            tipRecorded={tipRecorded}
            handleMethodSelect={handleMethodSelectBack}
          />
        );
      case PageState.EDIT_AMOUNT:
        return (
          <RecordPaymentEditAmount
            invoiceData={invoiceData}
            onBack={handleEditAmountBack}
            onSave={handleEditAmountSave}
            initialAmount={selectedAmount}
          />
        );
      case PageState.SUCCESS:
        return (
          <RecordPaymentSuccess
            isRootState={isRootState}
            handleSidePanelBack={handleCancelShareInvoice}
            recordedAmount={recordedPaymentAmount}
            onShareReceipt={onShareReceipt}
            handleCancelShareInvoice={handleCancelShareInvoice}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <SidePanel isOpen onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
        <div className="h-full w-96">
          {fetchingInvoiceData ? (
            <div className="flex h-full w-full items-center justify-center">
              <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
            </div>
          ) : (
            invoiceDataSuccess && (
              <section
                className={
                  'w-full right-0 ' +
                  'absolute bg-white h-full shadow-xl ' +
                  'delay-400 duration-500 ease-in-out transition-all transform overflow-auto'
                }>
                {renderCurrentScreen()}
              </section>
            )
          )}
        </div>
      </SidePanel>
      {showTipModal && (
        <RecordTipModal
          isOpen={showTipModal}
          onClose={handleTipModalClose}
          onTipRecorded={handleTipRecorded}
          invoiceData={invoiceData}
          selectedAmount={selectedAmount}
          tipRecorded={tipRecorded}
        />
      )}
    </>
  );
};

export default RecordPaymentPanel;
